import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { type Agendamento } from "@/services/app/agendamento";
import { Activity, ArrowLeft, Cat, ChevronRight, Clock, Dog, FileText, Info } from "lucide-react";
import { useState } from "react";

type AgendamentosDiaProps = {
  data?: {
    agendas: Agendamento[];
    data: {
      fim: string;
      hoje: string;
      inicio: string;
    };
  };
  isLoading?: boolean;
};

export function AgendamentosDia({ data = {agendas:  [], data: {fim: '', hoje: '', inicio: ''}}, isLoading = false }: AgendamentosDiaProps) {
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState<Agendamento | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  
  // Estado para controlar a visualização dentro do modal (lista ou detalhes)
  const [modalView, setModalView] = useState<'lista' | 'detalhes'>('lista');
  
  // Função para selecionar um agendamento e mostrar detalhes
  const selecionarAgendamento = (agendamento: Agendamento) => {
    setAgendamentoSelecionado(agendamento);
    setModalView('detalhes');
  };
  
  // Voltar para a lista de agendamentos
  const voltarParaLista = () => {
    setModalView('lista');
    setAgendamentoSelecionado(null);
  };

  // Fechar o sheet
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setModalView('lista');
      setAgendamentoSelecionado(null);
    }
  };

  // Componente para renderizar cada agendamento
  const AgendamentoItem = ({ agendamento }: { agendamento: Agendamento }) => (
    <button
      onClick={() => selecionarAgendamento(agendamento)}
      className="flex items-center justify-between bg-white p-2 mb-3 rounded-xl border border-gray-200 w-full text-left hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center">
        <div className={cn(
          "rounded-full p-3 mr-3",
          agendamento.pet.especie === 'CACHORRO' ? 'bg-blue-100' : 'bg-red-100'
        )}>
          {agendamento.pet.especie === 'CACHORRO' ? (
            <Dog size={24} className="text-blue-600" />
          ) : (
            <Cat size={24} className="text-red-500" />
          )}
        </div>
        <div>
          <p className="text-gray-900 font-medium text-base">{agendamento.pet.nome}</p>
          <p className="text-gray-500 text-sm">{agendamento.tipo.nome}</p>
        </div>
      </div>
      <ChevronRight size={20} className="text-gray-400" />
    </button>
  );


  if(isLoading) {
    return (
      <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if(data.agendas.length === 0) {
    return (
      <div className="bg-blue-50 rounded-xl p-2 border border-blue-100">
        <div className="flex items-center">
          <div className="bg-blue-100 rounded-full p-2 mr-3">
            <Info size={20} className="text-blue-600" />
          </div>
          <p className="text-blue-600 font-medium flex-1 text-sm sm:text-base">
            Você não tem agendamentos para hoje
          </p>
        </div>
      </div>
    );
  }



  return (
    <div>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <button className="bg-blue-50 rounded-xl p-2 border border-blue-100 w-full text-left hover:bg-blue-100/50 transition-colors">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <Info size={20} className="text-blue-600" />
              </div>
              <p className="text-blue-600 font-medium flex-1 text-sm sm:text-base">
                Você tem {data.agendas.length} agendamentos para hoje
              </p>
              <ChevronRight size={20} className="text-blue-600" />
            </div>
          </button>
        </SheetTrigger>
        
        <SheetContent side="bottom" className="max-h-[80vh] overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Visualização da lista de agendamentos */}
            {modalView === 'lista' && (
              <>
                <SheetHeader className="mb-6">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-2xl font-bold text-gray-800">
                      Agendamentos do Dia
                    </SheetTitle>
                  </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {data.agendas.map((agendamento) => (
                    <AgendamentoItem key={agendamento.id} agendamento={agendamento} />
                  ))}
                </div>
              </>
            )}

            {/* Visualização dos detalhes do agendamento */}
            {modalView === 'detalhes' && agendamentoSelecionado && (
              <>
                <SheetHeader className="mb-6">
                  <div className="flex items-center">
                    <button 
                      onClick={voltarParaLista}
                      className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <ArrowLeft size={24} className="text-blue-600" />
                    </button>
                    <SheetTitle className="text-xl font-bold text-gray-800 flex-1">
                      Detalhes do Agendamento
                    </SheetTitle>
                  </div>
                </SheetHeader>

                <div className="bg-gray-50 p-2 rounded-xl mb-6">
                  <div className="flex items-center">
                    <div className={cn(
                      "rounded-full p-2 mr-4",
                      agendamentoSelecionado.pet.especie === 'CACHORRO' ? 'bg-blue-100' : 'bg-red-100'
                    )}>
                      {agendamentoSelecionado.pet.especie === 'CACHORRO' ? (
                        <Dog size={32} className="text-blue-600" />
                      ) : (
                        <Cat size={32} className="text-red-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{agendamentoSelecionado.pet.nome}</h3>
                      <p className="text-gray-500">
                        {agendamentoSelecionado.pet.especie === 'CACHORRO' ? 'Cachorro' : 'Gato'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {agendamentoSelecionado.data && agendamentoSelecionado.status === 'AGENDADA' && (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                        <Clock size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Agendado para</p>
                        <p className="text-gray-900 font-medium">
                          {new Date(agendamentoSelecionado.data).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <Activity size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Serviço</p>
                      <p className="text-gray-900 font-medium">{agendamentoSelecionado.tipo.nome}</p>
                    </div>
                  </div>

                  {agendamentoSelecionado.observacao && (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                        <FileText size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Observação</p>
                        <p className="text-gray-900 font-medium">{agendamentoSelecionado.observacao}</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}